// Pure helpers for #193 (task → lead_treatment auto-bind) and #174
// (SCHEDULE_REMINDER preset menu).
// Keeping these out of TaskSlideout.jsx so they're trivially unit-testable
// without dragging in MUI / Redux / Vite-only modules.

// "Active" = lead_treatment whose status is neither CLOSED nor ARCHIVED.
// Status comparison is case-insensitive; entries with a missing/blank
// status are excluded (we only auto-bind when the BE clearly considers
// the row live).
export function pickActiveTreatments(lead) {
    const list = Array.isArray(lead?.lead_treatments) ? lead.lead_treatments : [];
    return list.filter((t) => {
        const status = (t?.status || '').toUpperCase();
        return status && status !== 'CLOSED' && status !== 'ARCHIVED';
    });
}

// Returns the only active treatment when the auto-bind branch should fire
// (exactly one active), otherwise null. Used by the slideout's
// open-effect so the call site stays readable.
export function getAutoBindTreatment(lead) {
    const active = pickActiveTreatments(lead);
    return active.length === 1 ? active[0] : null;
}

// Resolves the lead_treatment a task's Follow Up / hub popup must act on.
// The task's own FK (`task.lead_treatment`) is authoritative. That FK can arrive
// as a slim object (or bare id) WITHOUT `status`; the old code then fell through
// to "most recently updated treatment on the lead", which on a multi-treatment
// lead is often a DIFFERENT treatment (e.g. a TREATMENT_STARTED one while the
// task's own treatment is completed) and opened the wrong hub. So: hydrate the
// FK from task.lead.lead_treatments by id first; only guess "latest" when the
// task has no linked treatment at all.
export function resolveTaskTreatment(task) {
    const list = Array.isArray(task?.lead?.lead_treatments) ? task.lead.lead_treatments : [];
    const linked = task?.lead_treatment;
    const linkedId = linked != null && typeof linked === 'object' ? linked.id : linked;
    if (linkedId != null && linkedId !== '') {
        const full = list.find((t) => String(t?.id) === String(linkedId));
        if (full) return { ...(typeof linked === 'object' ? linked : {}), ...full };
        if (typeof linked === 'object') return linked;
        return null;
    }
    if (list.length === 0) return null;
    return [...list].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
}

// Treatment for a GROUP of tasks (a lead's tasks collapsed under one header row).
// Uses each task's own linked treatment (task.lead_treatment, hydrated by id).
// - all linked tasks agree on one treatment  -> that treatment
// - linked tasks point at DIFFERENT treatments -> null (ambiguous: show nothing
//   rather than one treatment's name/colour for another's tasks)
// - no task carries a link (older API responses) -> legacy guess: the lead's
//   most recently updated treatment.
export function resolveGroupTreatment(tasks, lead) {
    const list = Array.isArray(tasks) ? tasks : [];
    const linked = list
        .filter((t) => t?.lead_treatment != null)
        .map((t) => resolveTaskTreatment({ ...t, lead: t.lead || lead }))
        .filter(Boolean);
    if (linked.length > 0) {
        const ids = new Set(linked.map((t) => String(t.id)));
        return ids.size === 1 ? linked[0] : null;
    }
    const all = Array.isArray(lead?.lead_treatments) ? lead.lead_treatments : (list[0]?.lead?.lead_treatments || []);
    if (all.length === 0) return null;
    return [...all].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
}
